'use client';

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Award, Target, TrendingUp, Lightbulb } from 'lucide-react';

interface QuizCompletionScreenProps {
  score: number; // Percentage 0-100
  correctAnswers: number;
  totalQuestions: number;
  passed: boolean;
  onContinue: () => void;
  onRetry?: () => void; // Optional retry callback for first failure
  seedReward?: number; // Optional seed reward for completing the module
  onClose: () => void;
  attemptCount?: number; // Number of quiz attempts (1 = first try, 2 = retry)
}

// Confetti particle component
const Particle = ({
  delay,
  duration,
  startX
}: {
  delay: number;
  duration: number;
  startX: number;
}) => (
  <motion.div
    initial={{
      y: -20,
      x: startX,
      opacity: 1,
      scale: 1,
      rotate: 0
    }}
    animate={{
      y: [0, 100, 200, 300, 400],
      x: [startX, startX + (Math.random() - 0.5) * 200, startX + (Math.random() - 0.5) * 300],
      opacity: [1, 1, 0.8, 0.4, 0],
      rotate: [0, 180, 360, 540, 720],
      scale: [1, 0.8, 0.6, 0.4, 0.2]
    }}
    transition={{
      duration,
      delay,
      ease: 'easeOut'
    }}
    className="absolute"
    style={{
      width: Math.random() * 10 + 5,
      height: Math.random() * 10 + 5,
      backgroundColor: `hsl(${Math.random() * 360}, 70%, 60%)`,
      borderRadius: Math.random() > 0.5 ? '50%' : '0%'
    }}
  />
);

// Star burst for failed attempts (encouraging)
const StarBurst = ({ delay }: { delay: number }) => (
  <motion.div
    initial={{ scale: 0, rotate: 0, opacity: 0 }}
    animate={{
      scale: [0, 1.2, 1],
      rotate: [0, 180],
      opacity: [0, 1, 1]
    }}
    transition={{
      duration: 0.6,
      delay,
      ease: 'backOut'
    }}
    className="absolute text-orange-400"
    style={{
      top: `${Math.random() * 60 + 20}%`,
      left: `${Math.random() * 60 + 20}%`
    }}
  >
    <TrendingUp size={Math.random() * 20 + 15} />
  </motion.div>
);

export const QuizCompletionScreen: React.FC<QuizCompletionScreenProps> = ({
  score,
  correctAnswers,
  totalQuestions,
  passed,
  onContinue,
  onRetry,
  seedReward,
  onClose,
  attemptCount = 1
}) => {
  const canRetry = !passed && attemptCount === 1 && onRetry;
  const isSecondFailure = !passed && attemptCount >= 2;
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (passed) {
      setShowConfetti(true);
      // Stop confetti after 3 seconds
      const timer = setTimeout(() => setShowConfetti(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [passed]);

  // Generate confetti particles
  const confettiParticles = Array.from({ length: 50 }, (_, i) => (
    <Particle
      key={i}
      delay={Math.random() * 0.5}
      duration={2 + Math.random()}
      startX={Math.random() * 100 - 50}
    />
  ));

  // Generate star bursts for not quite passing
  const starBursts = Array.from({ length: 8 }, (_, i) => <StarBurst key={i} delay={i * 0.1} />);

  return (
    <div className="relative bg-white rounded-lg p-8 text-center overflow-hidden">
      {/* Particle effects container */}
      {showConfetti && passed && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="relative w-full h-full flex items-start justify-center">
            {confettiParticles}
          </div>
        </div>
      )}

      {!passed && (
        <div className="absolute inset-0 pointer-events-none overflow-hidden">{starBursts}</div>
      )}

      {/* Main content */}
      <motion.div
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          type: 'spring',
          stiffness: 200,
          damping: 15,
          delay: 0.2
        }}
      >
        {/* Icon */}
        <motion.div
          className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-4 ${
            passed ? 'bg-green-100' : 'bg-orange-100'
          }`}
          animate={
            passed
              ? {
                  scale: [1, 1.1, 1],
                  rotate: [0, 5, -5, 0]
                }
              : {
                  scale: [1, 1.05, 1]
                }
          }
          transition={{
            duration: 0.6,
            delay: 0.5,
            repeat: passed ? 2 : 0
          }}
        >
          {passed ? (
            <Trophy className="w-10 h-10 text-green-600" />
          ) : (
            <Target className="w-10 h-10 text-orange-600" />
          )}
        </motion.div>

        {/* Title */}
        <motion.h2
          className={`text-3xl font-bold mb-2 ${passed ? 'text-green-900' : 'text-orange-900'}`}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          {passed ? '🎉 Quiz Completed!' : '📚 Keep Learning!'}
        </motion.h2>

        {/* Score */}
        <motion.div
          className="mb-6"
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          <div className="flex items-center justify-center gap-4 mb-3">
            <motion.div
              className={`text-6xl font-bold ${passed ? 'text-green-600' : 'text-orange-600'}`}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{
                type: 'spring',
                stiffness: 300,
                damping: 10,
                delay: 0.7
              }}
            >
              {score}%
            </motion.div>
          </div>

          <p className="text-lg text-gray-700 mb-2">
            You got <span className="font-bold">{correctAnswers}</span> out of{' '}
            <span className="font-bold">{totalQuestions}</span> correct
          </p>

          {/* Pass/Fail message */}
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.9 }}
            className={`inline-block px-6 py-2 rounded-full font-medium ${
              passed
                ? 'bg-green-100 text-green-800 border border-green-200'
                : 'bg-orange-100 text-orange-800 border border-orange-200'
            }`}
          >
            {passed ? (
              <span className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                You Passed! (70% required)
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Keep Going! (70% to pass)
              </span>
            )}
          </motion.div>
        </motion.div>

        {/* Feedback message */}
        <motion.div
          className={`p-4 rounded-lg mb-6 ${
            passed ? 'bg-green-50 border border-green-200' : 'bg-blue-50 border border-blue-200'
          }`}
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1 }}
        >
          <p className={`text-sm ${passed ? 'text-green-800' : 'text-blue-800'}`}>
            {passed
              ? "Great job! You've demonstrated a strong understanding of the material. Keep up the excellent work! 🌟"
              : canRetry
                ? "Almost there! You get one more chance. We'll take you back through the material so you can review before trying the quiz again. 🎯"
                : "Don't worry! Learning is a journey. Review the material and try again when you're ready. You've got this! 💪"}
          </p>
        </motion.div>

        {/* Action buttons */}
        <div className="space-y-3">
          {/* Retry button (only on first failure) */}
          {canRetry && (
            <motion.button
              onClick={onRetry}
              className="w-full px-8 py-4 rounded-lg font-semibold text-white text-lg shadow-lg bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.1 }}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              🔄 Retry Lesson Now
            </motion.button>
          )}

          {/* Continue/Exit button */}
          <motion.button
            onClick={onContinue}
            className={`w-full px-8 py-4 rounded-lg font-semibold text-lg shadow-lg ${
              passed
                ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white'
                : canRetry
                  ? 'bg-gray-100 text-gray-600 hover:bg-gray-200 border border-gray-200'
                  : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white'
            }`}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: canRetry ? 1.2 : 1.1 }}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {passed && seedReward
              ? `Claim ${seedReward} ${seedReward === 1 ? 'Seed' : 'Seeds'} →`
              : passed
                ? 'Continue Learning →'
                : canRetry
                  ? 'Skip Retry & Exit'
                  : 'Try Again Later'}
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};
